import { emailButtonLabel } from 'owa-locstrings/lib/strings/emailbuttonlabel.locstring.json';
import {
    searchResultsFromArchive,
    junkMailHeader,
    singleItem,
    noneOrMultipleItems,
    recoverItemsDeletedFromThisFolder,
    goBackToDeletedItems,
    personaHeaderViewProfilebutton,
} from './getMailListHeader.locstring.json';
import { searchResults } from 'owa-locstrings/lib/strings/searchresults.locstring.json';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import MailListActionHeaderThirdRow from 'owa-mail-list-view/lib/components/listHeaders/MailListActionHeaderThirdRow';
import MailListFolderHeaderSecondRowContent from 'owa-mail-list-view/lib/components/listHeaders/MailListFolderHeaderSecondRowContent';
import MailListHeader from 'owa-mail-list-view/lib/components/MailListHeader';
import MailListHeaderSecondRowTextContent from 'owa-mail-list-view/lib/components/listHeaders/MailListHeaderSecondRowTextContent';
import MailListHeaderSelectionRowContent from 'owa-mail-list-view/lib/components/listHeaders/MailListHeaderSelectionRowContent';

import { SearchTableQuery } from 'owa-mail-list-search';
import { ActionSource, isDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import { BulkActionProgressBar } from 'owa-mail-bulk-action-view';

import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { MailListCategoryHeaderSecondRowContent } from 'owa-mail-category-view';
import {
    PersonaHeader,
    MultiPersonaHeader,
    HeaderButtonData,
} from 'owa-people-persona-header-view';
import { selectFolder } from 'owa-mail-folder-forest-actions';
import { listViewStore, TableQueryType, TableView } from 'owa-mail-list-store';
import { SearchHeaderFirstRowContent } from 'owa-mail-search';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getHeaderPersonaData } from '../selectors/getHeaderPersonaData';
import { getMultiPersonaHeaderData } from '../selectors/getMultiPersonaHeaderData';
import shouldShowCategoryHeader from '../selectors/shouldShowCategoryHeader';
import { isFolderUnderArchiveRoot } from 'owa-mail-store';
import { shouldShowSinglePersonaHeader } from '../selectors/shouldShowSinglePersonaHeader';
import { shouldShowMultiPersonaHeader } from '../selectors/shouldShowMultiPersonaHeader';
import { FolderForestNodeType } from 'owa-favorites-types';
import folderStore, {
    PRIMARY_DUMPSTER_DISTINGUISHED_ID,
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID,
    getEffectiveFolderDisplayName,
} from 'owa-folders';
import { SearchScopeKind } from 'owa-search-service';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { IsShadowMailboxUser } from 'owa-mail-ads-shared/lib/sharedAdsUtils';
import { ControlIcons } from 'owa-control-icons';
import { logUsage } from 'owa-analytics-actions';
import { lazyNewMessage } from 'owa-mail-message-actions';
import { GroupHeaderV2 } from 'owa-group-header-view';
import { getCurrentGroupInformationStore } from 'owa-groups-shared-store/lib/CurrentGroupInformationStore';
import { isEmailAddressBrand } from 'owa-persona/lib/utils/isEmailAddressBrand';
import { ErrorBoundary, ErrorComponentType } from 'owa-error-boundary';
import { isBulkActionValid } from 'owa-bulk-action-store';
import { isReadingPanePositionOff } from 'owa-mail-layout';

/**
 * Gets the appropriate mail list header based on the tableView properties
 * @param tableViewId tableView id
 * @param styleSelectorAsPerUserSettings css styles for the component when density flight is enabled
 * @return returns a header component
 */
export default function getMailListHeader(
    tableViewId: string,
    styleSelectorAsPerUserSettings: string
): JSX.Element {
    const tableView = listViewStore.tableViews.get(tableViewId);
    const selectedNode = getSelectedNode();
    const isSearchTable = tableView.tableQuery.type == TableQueryType.Search;
    const shouldShowSearchHeader =
        isSearchTable || selectedNode.type == FolderForestNodeType.Search;

    if (tableView.tableQuery.type == TableQueryType.Group) {
        const groupId = getCurrentGroupInformationStore().smtpAddress;
        if (isFeatureEnabled('grp-groupHeaderV2')) {
            return <GroupHeaderV2 groupId={groupId} />;
        }

        // TODO 89653: Add Group Header with Filters
        return null;
    }

    // When reading pane is hidden the selection related information needs to be shown
    // in selection header on top of list view
    const shouldShowSelectionHeader =
        isReadingPanePositionOff() &&
        ((tableView.isInCheckedMode && tableView.selectedRowKeys.size > 0) ||
            tableView.isInVirtualSelectAllMode);

    if (shouldShowMultiPersonaHeader(isSearchTable, tableViewId, selectedNode)) {
        const multiHeaderData = getMultiPersonaHeaderData(isSearchTable, tableViewId, selectedNode);

        return multiHeaderData ? (
            <ErrorBoundary
                child={
                    <MultiPersonaHeader
                        displayName={multiHeaderData.displayName}
                        personas={multiHeaderData.personas}
                        favoriteActionSource={multiHeaderData.origin}
                        pdlId={multiHeaderData.pdlId}
                        owsPersonaId={multiHeaderData.owsPersonaId}
                        hideFilter={isFeatureEnabled('sea-filters') ? false : isSearchTable}
                        isInSearch={shouldShowSearchHeader}
                    />
                }
                type={
                    isFeatureEnabled('fwk-devTools')
                        ? ErrorComponentType.Full
                        : ErrorComponentType.None
                }
            />
        ) : null;
    }

    if (shouldShowSinglePersonaHeader(isSearchTable, tableViewId, selectedNode)) {
        const headerPersona = getHeaderPersonaData(isSearchTable, tableViewId, selectedNode);

        // VSO 23726 - Refactor Persona header to use MailListHeader and only have custom content defined as components
        return (
            <ErrorBoundary
                child={
                    <PersonaHeader
                        hideFilter={isFeatureEnabled('sea-filters') ? false : isSearchTable}
                        personaId={headerPersona.personaId}
                        personEmail={headerPersona.email}
                        personName={headerPersona.displayName}
                        favoriteActionSource={headerPersona.origin}
                        mailboxType={headerPersona.mailboxType}
                        headerButtonData={getActionButtonData(
                            headerPersona.email,
                            headerPersona.displayName
                        )}
                        isInSearch={shouldShowSearchHeader}
                        headerActionsStylesAsPerUserSettings={styleSelectorAsPerUserSettings}
                    />
                }
                type={
                    isFeatureEnabled('fwk-devTools')
                        ? ErrorComponentType.Full
                        : ErrorComponentType.None
                }
            />
        );
    }

    let renderFirstRow: (mailListFirstRowCssClass: string) => any = null;
    let renderSecondRowContent: (containerCssClass: string) => any = null;
    let renderThirdRow: (mailListThirdRowCssClass: string) => any = null;
    let renderBulkActionProgressBar: () => any = null;
    let renderSecondRowIcon: (iconCssClass: string) => any = null;
    let shouldShowFilterMenu: boolean = true;
    let shouldShowSecondRowLeftEntity: boolean = true;
    const filterMenuSource: ActionSource = 'MailFilterMenu';

    // We pass categoryHeaderData so that we do not run the same logic twice
    // first to determine if the header should be shown and next to determine the name of the category
    const categoryHeaderData = { categoryName: null };
    if (shouldShowCategoryHeader(tableView, categoryHeaderData)) {
        shouldShowFilterMenu = false;

        if (shouldShowSearchHeader) {
            renderFirstRow = (mailListFirstRowCssClass: string) => {
                return React.createElement(SearchHeaderFirstRowContent, {
                    className: mailListFirstRowCssClass,
                    showSpeller: false,
                });
            };
        }

        const categoryName = categoryHeaderData.categoryName;
        if (shouldShowSelectionHeader) {
            renderSecondRowContent = getSelectionHeader(tableView);
        } else {
            renderSecondRowContent = (containerCssClass: string) => {
                return React.createElement(
                    MailListCategoryHeaderSecondRowContent,
                    {
                        key: categoryName,
                        categoryName: categoryName,
                        containerCssClass: containerCssClass,
                    },
                    null /* children */
                );
            };
        }
    } else if (shouldShowSearchHeader) {
        shouldShowFilterMenu = isFeatureEnabled('sea-filters');

        renderFirstRow = (mailListFirstRowCssClass: string) => {
            return React.createElement(SearchHeaderFirstRowContent, {
                className: mailListFirstRowCssClass,
            });
        };

        if (shouldShowSelectionHeader) {
            renderSecondRowContent = getSelectionHeader(tableView);
        } else {
            renderSecondRowContent = (containerCssClass: string) => {
                const searchScope = (tableView.tableQuery as SearchTableQuery).searchScope;
                const searchResultHeaderText =
                    searchScope && searchScope.kind == SearchScopeKind.ArchiveMailbox
                        ? loc(searchResultsFromArchive)
                        : loc(searchResults);
                return React.createElement(
                    MailListHeaderSecondRowTextContent,
                    { text: searchResultHeaderText, containerCssClass: containerCssClass },
                    null /* children */
                );
            };
        }
    } else {
        const folderId = tableView.tableQuery.folderId;
        const isDeletedTable = isDeletedItemsFolderFromAnyMailbox(folderId);
        const recoverDeletedItemsEnabled = getUserConfiguration().RecoverDeletedItemsEnabled;
        let dumpsterFolderDistinguishedId;
        let dumpsterFolderId;
        let deletedItemsFolderDistinguishedId;
        let treeType;
        if (isFolderUnderArchiveRoot(folderId)) {
            dumpsterFolderDistinguishedId = ARCHIVE_DUMPSTER_DISTINGUISHED_ID;
            dumpsterFolderId = folderNameToId(ARCHIVE_DUMPSTER_DISTINGUISHED_ID);
            treeType = 'archiveFolderTree';
            deletedItemsFolderDistinguishedId = ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID;
        } else {
            dumpsterFolderDistinguishedId = PRIMARY_DUMPSTER_DISTINGUISHED_ID;
            dumpsterFolderId = folderNameToId(PRIMARY_DUMPSTER_DISTINGUISHED_ID);
            treeType = 'primaryFolderTree';
            deletedItemsFolderDistinguishedId = PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID;
        }
        const isDumpsterTable = dumpsterFolderId === folderId;
        const isNotesFolder = folderIdToName(folderId) === 'notes';

        shouldShowFilterMenu = !isDumpsterTable && !isNotesFolder;

        /**
         * Only show junk email folder header if we're in "Junk Email" folder. In
         * Cloud Cache mailboxes, the junk email folder is called "Spam" so we
         * shouldn't show it.
         */
        if (folderIdToName(folderId) == 'junkemail' && !IsShadowMailboxUser()) {
            const retentionPolicyTags = getUserConfiguration().RetentionPolicyTags;

            let retentionDays = 30; // Default
            for (const policy of retentionPolicyTags) {
                if (policy.DisplayName == 'JunkMail') {
                    retentionDays = policy.RetentionPeriod;
                    break;
                }
            }

            renderThirdRow = (mailListThirdRowCssClass: string) => {
                return React.createElement(
                    MailListActionHeaderThirdRow,
                    {
                        containerCssClass: mailListThirdRowCssClass, // and disable blue text?
                        text: format(loc(junkMailHeader), retentionDays),
                    },
                    null /* children */
                );
            };
        }

        if (isDeletedTable && recoverDeletedItemsEnabled) {
            const dumpsterTotalCount = folderStore.folderTable.get(dumpsterFolderId).TotalCount;
            let dumpsterLinkNumItemsString;
            let showLoadingSpinner = false;

            if (dumpsterTotalCount < 0) {
                showLoadingSpinner = true;
            } else if (dumpsterTotalCount === 1) {
                dumpsterLinkNumItemsString = format(loc(singleItem), dumpsterTotalCount);
            } else {
                dumpsterLinkNumItemsString = format(loc(noneOrMultipleItems), dumpsterTotalCount);
            }

            let dumpsterLinkString = loc(recoverItemsDeletedFromThisFolder);
            // Currently, we don not show the number for archive mailbox because
            // the count gets updated by hierarchy notifications
            if (!showLoadingSpinner && treeType === 'primaryFolderTree') {
                // TODO: Task 26546: [RTL] Go to recoverable items in deleted items folder string needs to be RTL friendly
                dumpsterLinkString += ' (' + dumpsterLinkNumItemsString + ')';
            }

            renderThirdRow = (mailListThirdRowCssClass: string) => {
                return React.createElement(
                    MailListActionHeaderThirdRow,
                    {
                        containerCssClass: mailListThirdRowCssClass,
                        text: dumpsterLinkString,
                        onClick: goToDumpster,
                        showLoadingSpinner: showLoadingSpinner,
                        distinguishedFolderId: dumpsterFolderDistinguishedId,
                    },
                    null /* children */
                );
            };
        } else if (isDumpsterTable) {
            renderSecondRowContent = (containerCssClass: string) => {
                return React.createElement(
                    MailListHeaderSecondRowTextContent,
                    {
                        text: getEffectiveFolderDisplayName(
                            folderStore.folderTable.get(dumpsterFolderId)
                        ),
                        containerCssClass: containerCssClass,
                    },
                    null /* children */
                );
            };

            renderThirdRow = (mailListThirdRowCssClass: string) => {
                return React.createElement(
                    MailListActionHeaderThirdRow,
                    {
                        containerCssClass: mailListThirdRowCssClass,
                        text: loc(goBackToDeletedItems),
                        onClick: goToDeletedItems,
                        showBackArrow: true,
                        distinguishedFolderId: deletedItemsFolderDistinguishedId,
                    },
                    null /* children */
                );
            };
        }

        // This checks whether the second row has been set. This is possible if:
        // The dumpster flag is off OR
        // We are in a non-dumpster folder
        if (!renderSecondRowContent) {
            if (shouldShowSelectionHeader) {
                renderSecondRowContent = getSelectionHeader(tableView);
            } else {
                // don't show checkbox in second row for Sticky Notes experience in Notes folder
                if (isNotesFolder && isFeatureEnabled('notes-folder-view')) {
                    shouldShowSecondRowLeftEntity = false;
                }
                renderSecondRowContent = (containerCssClass: string) => {
                    return React.createElement(
                        MailListFolderHeaderSecondRowContent,
                        {
                            tableViewId: tableViewId,
                            containerCssClass: containerCssClass,
                            mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                        },
                        null /* children */
                    );
                };
            }
        }

        // Show bulk action progress bar there is a valid bulk operation running on that folder
        if (isBulkActionValid(folderId)) {
            renderBulkActionProgressBar = () => {
                return React.createElement(BulkActionProgressBar);
            };
        }
    }

    return React.createElement(
        MailListHeader,
        {
            tableViewId: tableViewId,
            shouldShowFilterMenu: shouldShowFilterMenu,
            shouldShowSecondRowLeftEntity: shouldShowSecondRowLeftEntity,
            filterMenuSource: filterMenuSource,
            renderFirstRow: renderFirstRow,
            renderSecondRowIcon: renderSecondRowIcon,
            renderSecondRowCustomContent: renderSecondRowContent,
            renderThirdRow: renderThirdRow,
            renderBulkActionProgressBar: renderBulkActionProgressBar,
            mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
        },
        null /* children */
    );
}

function getActionButtonData(personaEmail: string, personaName: string): HeaderButtonData[] {
    const actionButtonData: HeaderButtonData[] = [
        {
            icon: ControlIcons.Mail,
            text: loc(emailButtonLabel),
            clickCallback: () => handleEmailClick(personaEmail, personaName),
        },
    ];

    if (isFeatureEnabled('peo-ev-header')) {
        actionButtonData.push({
            icon: ControlIcons.ContactCard,
            text: loc(personaHeaderViewProfilebutton),
            isLPCWrapped: true,
            lpcClientScenario: 'PersonaHeaderProfileButton',
            personaEmail,
            personaName,
            lpcLocationToOpen: isEmailAddressBrand(personaEmail) ? undefined : 'ExpandedView',
        });
    }

    return actionButtonData;
}

async function handleEmailClick(personaEmail: string, personaName: string) {
    logUsage('MailComposeFromHeader');

    lazyNewMessage.importAndExecute('PersonaHeader', null /*groupId*/, [
        { EmailAddress: personaEmail, Name: personaName },
    ]);
}

function goToDumpster(event: any, distinguishedFolderId: string) {
    const treeType =
        distinguishedFolderId === ARCHIVE_DUMPSTER_DISTINGUISHED_ID
            ? 'archiveFolderTree'
            : 'primaryFolderTree';
    selectFolder(folderNameToId(distinguishedFolderId), treeType, 'Dumpster');
}

function goToDeletedItems(event: any, distinguishedFolderId: string) {
    const treeType =
        distinguishedFolderId === ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID
            ? 'archiveFolderTree'
            : 'primaryFolderTree';
    selectFolder(folderNameToId(distinguishedFolderId), treeType, 'Dumpster');
}

function getSelectionHeader(tableView: TableView) {
    return (containerCssClass: string) => {
        return React.createElement(
            MailListHeaderSelectionRowContent,
            {
                numChecked: tableView.selectedRowKeys.size,
                containerCssClass: containerCssClass,
                tableViewId: tableView.id,
                folderId: tableView.tableQuery.folderId,
            },
            null /* children */
        );
    };
}
